import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Inbox } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function SuratMasuk() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ letter_number: "", sender: "", subject: "", date_received: new Date().toISOString().slice(0,10), date_letter: "", disposition: "" });
  const canCreate = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","WAKIL_SEKRETARIS"].includes(user?.role);

  const load = () => api.get("/letters/incoming").then((r) => setItems(r.data)).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.letter_number || !form.sender || !form.subject) { toast.error("Nomor, pengirim & perihal wajib diisi."); return; }
    try {
      await api.post("/letters/incoming", form);
      toast.success("Surat masuk berhasil dicatat.");
      setOpen(false);
      setForm({ letter_number: "", sender: "", subject: "", date_received: new Date().toISOString().slice(0,10), date_letter: "", disposition: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Administrasi</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Surat Masuk</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Pencatatan, disposisi, dan tindak lanjut surat masuk Klasis.</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-letter"><Plus className="h-4 w-4 mr-2" /> Catat Surat</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Surat Masuk Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nomor surat</Label><Input value={form.letter_number} onChange={(e)=>setForm({...form, letter_number:e.target.value})} data-testid="input-letter-number" /></div>
                <div><Label>Pengirim</Label><Input value={form.sender} onChange={(e)=>setForm({...form, sender:e.target.value})} data-testid="input-letter-sender" /></div>
                <div><Label>Perihal</Label><Input value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} data-testid="input-letter-subject" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Tanggal surat</Label><Input type="date" value={form.date_letter} onChange={(e)=>setForm({...form, date_letter:e.target.value})} data-testid="input-letter-date-letter" /></div>
                  <div><Label>Tanggal diterima</Label><Input type="date" value={form.date_received} onChange={(e)=>setForm({...form, date_received:e.target.value})} data-testid="input-letter-date-received" /></div>
                </div>
                <div><Label>Disposisi</Label><Input value={form.disposition} onChange={(e)=>setForm({...form, disposition:e.target.value})} data-testid="input-letter-disposition" /></div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="btn-save-letter">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Memuat…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Inbox className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <div className="text-sm text-muted-foreground">Belum ada surat masuk.</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No. Agenda</TableHead>
                  <TableHead>No. Surat</TableHead>
                  <TableHead>Pengirim</TableHead>
                  <TableHead>Perihal</TableHead>
                  <TableHead>Diterima</TableHead>
                  <TableHead>Disposisi</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((l) => (
                  <TableRow key={l.id} data-testid={`row-letter-${l.id}`}>
                    <TableCell className="font-mono text-xs">{l.agenda_number}</TableCell>
                    <TableCell className="font-mono text-xs">{l.letter_number}</TableCell>
                    <TableCell>{l.sender}</TableCell>
                    <TableCell className="max-w-xs truncate">{l.subject}</TableCell>
                    <TableCell className="text-xs">{l.date_received}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.disposition || "—"}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
